import { ChevronDown, ChevronRight, Settings2 } from "lucide-react";
import clsx from "clsx";
import type { PlanSetupCount, PlanSetupRowProps } from "./types";

const MARK: Record<PlanSetupCount["tone"], string> = {
  neutral: "h-2 w-2 rounded-full bg-slate-400",
  removed: "h-2 w-2 rounded-full bg-rose-500",
  unrouted: "h-2 w-2 border-[1.5px] border-violet-600",
  tray: "h-1 w-3 rounded-full bg-slate-400",
  wall: "h-1.5 w-3 rounded-sm bg-stone-700",
  room: "h-2 w-3 rounded-[2px] border border-slate-500 bg-slate-200",
};

export const PlanSetupRow = ({
  title,
  counts,
  staleLabel,
  isOpen,
  onToggle,
}: PlanSetupRowProps) => (
  // The group's own card draws the border and the rounding: this is its HEADER,
  // not a card beside the sections it holds. Open, it takes the darker band —
  // the row and the four below it have to read as one thing.
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={isOpen}
    data-testid="plan-setup-row"
    className={clsx(
      "flex h-10 w-full flex-none items-center gap-2 px-2.5 text-left",
      staleLabel
        ? "bg-amber-50 hover:bg-amber-100"
        : isOpen
          ? "bg-slate-200 hover:bg-slate-300"
          : "bg-white hover:bg-slate-50",
    )}
  >
    <Settings2 size={15} aria-hidden className="flex-none text-slate-500" />
    <span className="min-w-0 truncate text-[14px] font-semibold text-slate-800">
      {title}
    </span>
    {/* What is inside, without opening it: the marks are the ones the plan
        draws, so the row says four removals and two unrouted at a glance. */}
    <span className="ml-auto flex flex-none items-center gap-2">
      {staleLabel ? (
        <span className="truncate text-[13px] font-semibold text-amber-800">
          {staleLabel}
        </span>
      ) : (
        counts.map((entry) => (
          <span
            key={entry.tone}
            title={entry.label}
            className="flex items-center gap-1 text-[13px] tabular-nums text-slate-600"
          >
            <span aria-hidden className={clsx("flex-none", MARK[entry.tone])} />
            {entry.count}
            <span className="sr-only">{entry.label}</span>
          </span>
        ))
      )}
      <span className="flex-none text-slate-500">
        {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
      </span>
    </span>
  </button>
);
